import React, { useState, useRef, useEffect, useContext } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "./styles/Navbar.css";
import { useAuth } from "../providers/AuthProvider";
import config from "../configs/config";
import {
  FaCode,
  FaCheckCircle,
  FaTable,
  FaList,
  FaCalculator,
  FaChartBar,
  FaThLarge,
  FaHome,
  FaClipboardList,
  FaInfoCircle,
  FaEdit,
  FaUserShield,
} from "react-icons/fa";
import ProjectDetails from "../components/ProjectDetails";
import VariableContext from "../contexts/VariableContext";

function Navbar() {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [orgLogo, setOrgLogo] = useState(null);
  const [showProjectDetailsModal, setShowProjectDetailsModal] = useState(false);

  // Add responsive state for mobile menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // --- Prompts modal state ---
  const [showPromptsModal, setShowPromptsModal] = useState(false);
  const [prompts, setPrompts] = useState({ prompt1: "", prompt2: "", prompt3: "" });
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [promptsSaveStatus, setPromptsSaveStatus] = useState({ type: "", message: "" });
  const [promptsViewMode, setPromptsViewMode] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const {
    setOriginalDataRows,
    selectedProjectDetails,
    setSelectedProjectDetails,
    loading,
    setLoading,
    setBanners,
    setdataGroup,
    dataGroup,
    projectId,
    setProjectId,
    setWeightingData,
    setWeightingStats,
    setcurrentSelectedVariablesset,
    setSavedWeightings,
    setVariables,
    setSelectedClient,
    setSelectedProject,
    selectedProject,
    selectedClient,
    setSdataGroup,
    powner,
    setPowner,
    setSurveyFileName,
    setSurveyName,
  } = useContext(VariableContext);

  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const hideLinksOnPaths = ["/home", "/login"];

  // Define admin users
  const ADMIN_USERS = [
    "amit@excavateresearch.com",
    "shreekant.panchal@excavateresearch.com",
    "anshu.sinha@excavateresearch.com",
    "arpan.chauhan@excavateresearch.com"
  ];

  const navItems = [
    { path: "/home", label: "Home", feature: "home", icon: <FaHome /> },
    { path: "/surveybuilder", label: "Scripting", feature: "scripting", icon: <FaCode /> },
    { path: "/datacheck", label: "Data Validate", feature: "datacheck", icon: <FaCheckCircle /> },
    { path: "/datatables", label: "Data Tables", feature: "datatables", icon: <FaTable /> },
    { path: "/banner", label: "Banner", feature: "banner", icon: <FaList /> },
    { path: "/weighting", label: "Weighting", feature: "weighting", icon: <FaCalculator /> },
    { path: "/dataanalysis", label: "Data Analysis", feature: "dataanalysis", icon: <FaChartBar /> },
    { path: "/dashboardbuilder", label: "Dashboard", feature: "dashboard", icon: <FaThLarge /> },
    { path: "/qnrdesign", label: "Qnr Design", feature: "qnrdesign", icon: <FaClipboardList /> },
  ];

  // Check if current user is admin
  const userEmail = user?.user?.email || user?.email || "";
  const isAdmin = ADMIN_USERS.includes(userEmail);

  // Only add Admin nav item if user is admin
  if (isAdmin) {
    navItems.push({
      path: "/orgadmin",
      label: "Admin",
      feature: "orgadmin",
      icon: <FaUserShield />,
    });
  }

  // Add this to your existing useEffects in the Navbar component
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMobileMenuOpen]);

  // Responsive detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);

      // Close mobile menu on resize to desktop
      if (width > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  const getInitial = (email) => (email ? email.charAt(0).toUpperCase() : "U");
  const getFullNameFromEmail = (email) => {
    if (!email) return "";
    const namePart = email.split("@")[0];
    const nameParts = namePart.split(/[._]/);
    return nameParts.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(" ");
  };

  const userName = getFullNameFromEmail(userEmail);

  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;
    if (organization?.logo_url) {
      setOrgLogo(organization.logo_url);
    }
  }, [user]);

  // Handle click outside for dropdown and mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Loading gate
  const features = user?.user?.organization?.features || user?.organization?.features;
  if (!features) {
    return <div className="loading-navbar">Loading...</div>;
  }

  // Filter nav items based on features
  const filteredNavItems = navItems.filter((item) => {
    if (item.path === "/orgadmin") return true;
    return features[item.feature] === true;
  });

  const closeProjectDetailsModal = () => setShowProjectDetailsModal(false);

  // --- Prompts handlers ---
  const fetchPrompts = async () => {
    setPromptsLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/prompts`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setPrompts(data);
      } else {
        console.error("Failed to fetch prompts");
      }
    } catch (error) {
      console.error("Error fetching prompts:", error);
    } finally {
      setPromptsLoading(false);
    }
  };

  const savePrompts = async () => {
    setPromptsSaveStatus({ type: "info", message: "Saving..." });
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/prompts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(prompts),
      });
      if (response.ok) {
        setPromptsSaveStatus({ type: "success", message: "Saved successfully!" });
        setTimeout(() => setPromptsSaveStatus({ type: "", message: "" }), 3000);
      } else {
        setPromptsSaveStatus({ type: "error", message: "Save failed" });
      }
    } catch (error) {
      setPromptsSaveStatus({ type: "error", message: "Network error" });
    }
  };

  const openPromptsModal = () => {
    setShowPromptsModal(true);
    setPromptsViewMode(false);
    fetchPrompts();
    setShowDropdown(false);
  };

  const closePromptsModal = () => {
    setShowPromptsModal(false);
    setPromptsSaveStatus({ type: "", message: "" });
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          {/* Logo Section */}
          <div className="navbar-brand">
            <img
              src={logoError ? '/default-logo.png' : (config.REACT_APP_LOGO_URL || orgLogo || '/default-logo.png')}
              alt="Organization Logo"
              className="logo"
              onError={(e) => {
                if (!logoError) {
                  setLogoError(true);
                  e.target.onerror = null;
                  e.target.src = '/default-logo.png';
                }
              }}
            />
          </div>

          {/* Mobile Menu Button */}
          {(isMobile || isTablet) && !hideLinksOnPaths.includes(location.pathname) && (
            <button
              className={`mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span className="menu-icon"></span>
              <span className="menu-icon"></span>
              <span className="menu-icon"></span>
            </button>
          )}

          {/* Navigation Links - Desktop */}
          {!hideLinksOnPaths.includes(location.pathname) && (
            <>
              <div className={`nav-links ${isMobile || isTablet ? 'mobile-nav' : ''} ${isMobileMenuOpen ? 'open' : ''}`} ref={mobileMenuRef}>
                {filteredNavItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="nav-icon">{item.icon}</div>
                    <span className="nav-label">{item.label}</span>
                    {item.badge && <small className="beta-tag">{item.badge}</small>}
                  </NavLink>
                ))}
              </div>
            </>
          )}

          {/* Profile Section */}
          <div className="profile-section" ref={dropdownRef}>
            <button
              className="project-details-btn"
              onClick={() => setShowProjectDetailsModal(true)}
              title="View Project Details"
            >
              <FaInfoCircle />
            </button>

            <div className="profile-avatar" onClick={() => setShowDropdown(!showDropdown)}>
              {getInitial(userEmail)}
            </div>

            {showDropdown && (
              <div className="profile-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-avatar">{getInitial(userEmail)}</div>
                  <div className="dropdown-info">
                    <div className="dropdown-name">{userName}</div>
                    <div className="dropdown-email">{userEmail}</div>
                  </div>
                </div>
                <div className="dropdown-divider"></div>

                <button className="dropdown-edit-prompts" onClick={openPromptsModal}>
                  <FaEdit style={{ marginRight: "8px" }} /> Edit System Prompts
                </button>

                <div className="dropdown-divider"></div>
                <button className="dropdown-logout" onClick={logout}>
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Project Details Modal */}
      {showProjectDetailsModal && (
        <div className="nb-modal-overlay" onClick={closeProjectDetailsModal}>
          <div className="nb-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="nb-modal-header">
              <h2>Project Details</h2>
              <button className="nb-modal-close-btn" onClick={closeProjectDetailsModal}>
                &times;
              </button>
            </div>
            <div className="nb-modal-body">
              <ProjectDetails project={selectedProjectDetails} projectid={projectId} projectOwner={powner} />
            </div>
            <div className="nb-modal-footer">

            </div>
          </div>
        </div>
      )}

      {/* Prompts Modal */}
      {showPromptsModal && (
        <div className="nb-modal-overlay" onClick={closePromptsModal}>
          <div className="nb-modal-content prompts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nb-modal-header">
              <h2>{promptsViewMode ? "View System Prompts" : "Edit System Prompts"}</h2>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button
                  className="nb-modal-toggle-mode-btn"
                  onClick={() => setPromptsViewMode(!promptsViewMode)}
                  title={promptsViewMode ? "Switch to Edit mode" : "Switch to View mode"}
                >
                  {promptsViewMode ? (
                    <>
                      <FaEdit style={{ marginRight: "6px" }} /> Edit
                    </>
                  ) : (
                    <>
                      <FaInfoCircle style={{ marginRight: "6px" }} /> View
                    </>
                  )}
                </button>
                <button className="nb-modal-close-btn" onClick={closePromptsModal}>
                  &times;
                </button>
              </div>
            </div>

            <div className="nb-modal-body">
              {promptsLoading ? (
                <div className="loading-spinner">Loading prompts...</div>
              ) : (
                <>
                  <div className="prompt-field">
                    <label>Data Tables Prompt (datatablesprompt.txt)</label>
                    {promptsViewMode ? (
                      <pre className="prompt-view-box">{prompts.prompt1 || "(empty)"}</pre>
                    ) : (
                      <textarea
                        rows={isMobile ? 8 : 6}
                        value={prompts.prompt1}
                        onChange={(e) => setPrompts({ ...prompts, prompt1: e.target.value })}
                        placeholder="Enter prompt for Data Tables..."
                      />
                    )}
                  </div>

                  <div className="prompt-field">
                    <label>Data Validation Prompt (datavalidationprompt.txt)</label>
                    {promptsViewMode ? (
                      <pre className="prompt-view-box">{prompts.prompt2 || "(empty)"}</pre>
                    ) : (
                      <textarea
                        rows={isMobile ? 8 : 6}
                        value={prompts.prompt2}
                        onChange={(e) => setPrompts({ ...prompts, prompt2: e.target.value })}
                        placeholder="Enter prompt for Data Validation..."
                      />
                    )}
                  </div>

                  <div className="prompt-field">
                    <label>Survey Scripting Prompt (surveyscriptingprompt.txt)</label>
                    {promptsViewMode ? (
                      <pre className="prompt-view-box">{prompts.prompt3 || "(empty)"}</pre>
                    ) : (
                      <textarea
                        rows={isMobile ? 8 : 6}
                        value={prompts.prompt3}
                        onChange={(e) => setPrompts({ ...prompts, prompt3: e.target.value })}
                        placeholder="Enter prompt for Survey Scripting..."
                      />
                    )}
                  </div>

                  {!promptsViewMode && promptsSaveStatus.message && (
                    <div className={`save-status ${promptsSaveStatus.type}`}>
                      {promptsSaveStatus.message}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="nb-modal-footer">
              {!promptsViewMode && (
                <button
                  className="nb-modal-save-button"
                  onClick={savePrompts}
                  disabled={promptsLoading}
                >
                  Save Changes
                </button>
              )}
              <button className="nb-modal-close-button" onClick={closePromptsModal}>
                {promptsViewMode ? "Close" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
